import type { ArtifactResult } from "~/services/aiOrchestrator";

import { Box, Button, Icon } from "@mui/material";
import { useEffect, useState } from "react";

import FernieCardHeader from "~/components/FernieCardHeader";
import FernieCardShell from "~/components/FernieCardShell";
import { BodyTextSmall, CaptionText } from "~/components/Typographies";
import downloadArtifact, {
	createArtifactObjectUrl,
	isHtmlArtifact,
	revokeArtifactObjectUrl
} from "~/helpers/aiChat/reportArtifacts";
import logger from "~/helpers/logger";

interface FernieArtifactCardProps {
	artifact: ArtifactResult;
}

const FernieArtifactCard = ({ artifact }: FernieArtifactCardProps) => {
	const isHtml = isHtmlArtifact(artifact);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	useEffect(() => {
		if (!isHtml) return;
		const url = createArtifactObjectUrl(artifact);
		setPreviewUrl(url);
		return () => revokeArtifactObjectUrl(url);
	}, [artifact, isHtml]);

	const openReport = () => {
		if (isHtml) {
			const url = createArtifactObjectUrl(artifact);
			if (url) {
				window.open(url, "_blank", "noopener,noreferrer");
				return;
			}
			logger.error("FernieArtifactCard: failed to open HTML report preview");
		}
		downloadArtifact(artifact);
	};

	return (
		<FernieCardShell accent="primary" branded tinted>
			<FernieCardHeader accent="primary" branded pillLabel="Report" />

			<Box sx={{ alignItems: "center", display: "flex", gap: 1, px: 2, pt: 1.25 }}>
				<Icon sx={{ color: "primary.main", fontSize: 18 }}>{isHtml ? "article" : "table_view"}</Icon>
				<CaptionText
					sx={{
						color: "text.secondary",
						flex: 1,
						fontWeight: 500,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap"
					}}
				>
					{artifact.filename}
				</CaptionText>
			</Box>

			{isHtml && previewUrl && (
				<Box
					sx={{
						backgroundColor: "common.white",
						borderColor: "divider",
						borderRadius: "10px",
						borderStyle: "solid",
						borderWidth: "1px",
						height: 180,
						mt: 1.25,
						mx: 2,
						overflow: "hidden",
						position: "relative"
					}}
				>
					<iframe
						sandbox=""
						src={previewUrl}
						style={{
							border: "none",
							height: 240,
							pointerEvents: "none",
							transform: "scale(0.75)",
							transformOrigin: "top left",
							width: "133%"
						}}
						title={artifact.filename}
					/>
					<Box
						sx={{
							background: (theme) => `linear-gradient(transparent 40%, ${theme.palette.common.white})`,
							inset: 0,
							position: "absolute"
						}}
					/>
				</Box>
			)}

			<Box sx={{ display: "flex", gap: 1, pb: 1.75, pt: 1.5, px: 2 }}>
				<Button
					color="primary"
					endIcon={<Icon sx={{ fontSize: 16 }}>arrow_forward</Icon>}
					onClick={openReport}
					sx={{ borderRadius: "8px", flex: 1, py: 0.85, textTransform: "none" }}
					variant="contained"
				>
					<BodyTextSmall bold sx={{ color: "inherit" }}>
						Open report
					</BodyTextSmall>
				</Button>
				<Button
					color="plain"
					onClick={() => downloadArtifact(artifact)}
					sx={{ borderRadius: "8px", color: "text.secondary", py: 0.85, textTransform: "none" }}
					variant="outlined"
				>
					<BodyTextSmall sx={{ color: "inherit", fontWeight: 500 }}>Export</BodyTextSmall>
				</Button>
			</Box>
		</FernieCardShell>
	);
};

export default FernieArtifactCard;
